import { useEffect, useState } from "react";
import Chart, { ChartItem } from "chart.js/auto";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Project } from "../../features/projects/Project";
import { Milestone } from "../../features/projects/Milestone";

const METRIC_TOOLTIPS: Record<string, string> = {
  "Total estimate":
    "The milestone budget (hours) from [ESTIMATED_HOURS] in the milestone description. This is the number communicated to the client.",
  "Actual burnup":
    "Cumulative actual hours logged in Clockify for this milestone. Shows real work done over time.",
  "Estimate burnup":
    "Projected hours completed based on task completion ratio: Total Estimate x (Completed Tasks / Total Tasks).",
  "Est. Total hours":
    "Legacy formula: Actual Burnup × (Milestone Estimate / Completed Task Estimates). A simple work-proportionality extrapolation that doesn't account for velocity or variance. The Monte Carlo forecast (blue lines on the chart) uses historical overrun multipliers from closed tasks to project remaining hours with P50/P80/P95 confidence bands.",
  "Total Task Estimate":
    "Sum of individual task time estimates (hours) for all tasks created up to each date.",
  "Total Of Est. Completed":
    "Sum of time estimates (hours) for tasks that have been closed/completed.",
  "Total Tasks":
    "Count of tasks created in this milestone up to each date.",
  "Gain From Estimate":
    "Estimate Burnup minus Actual Burnup. Positive (green) means you're ahead of the estimate; negative (red) means behind.",
  "Actual / Total Task Est.":
    "Ratio of actual hours worked to total task estimates. Below 1.0 = under budget per task, above 1.0 = over budget.",
  "Actual / Total Estimate":
    "Ratio of actual hours worked to the milestone estimate. Below 1.0 = under the client budget, above 1.0 = over budget.",
  "Actual / Estimate Burnup":
    "Ratio of actual hours to estimate burnup. Below 1.0 = completing tasks with less effort than estimated.",
};

interface ProjectProgressReportProps {
  projects: Project[];
  selectedProject: Project;
  selectedMilestone: Milestone;
  handleProjectChange?: (event: any) => void;
  handleMilestoneChange?: (event: any) => void;
}

const TaskProgressStrip = ({ total, open }: { total: number; open: number }) => {
  if (total <= 0) {
    return null;
  }
  const closed = Math.max(0, total - open);
  const segments = Array.from({ length: total }, (_, i) => i < closed);
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 0.75,
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
          Task completion
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {closed} / {total} closed · {open} open
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: "3px", width: "100%" }}>
        {segments.map((isClosed, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: "6px",
              borderRadius: "2px",
              backgroundColor: isClosed
                ? "rgba(75, 192, 192, 1)"
                : "rgba(0, 0, 0, 0.08)",
              transition: "background-color 0.2s",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const buildLegacyEstimateDataset = (selectedMilestone: Milestone) => {
  if (selectedMilestone?.forecast) {
    return [];
  }
  return [
    {
      label: "Est. Total Hours (ratio)",
      tension: 0.4,
      data:
        selectedMilestone?.estimatedTotalHoursNeeded?.map((item) => item.value) ||
        [],
      borderColor: "rgba(153, 102, 255, 1)",
      fill: false,
    },
  ];
};

const buildForecastDatasets = (selectedMilestone: Milestone, pointCount: number) => {
  const forecast = selectedMilestone?.forecast;
  if (!forecast || pointCount === 0) {
    return [];
  }

  const history = selectedMilestone?.forecastHistory ?? [];
  const chartDates = (selectedMilestone?.totalEstimate ?? []).map((p) =>
    new Date(p.date).getTime()
  );

  // One history point → flat horizontal reference line at that value.
  // Multiple history points → plot per chart date via carry-forward: each x
  // slot gets the most recent forecast whose date <= chart date. Leading
  // slots before the first forecast stay null (Chart.js skips nulls).
  const seriesFor = (
    selector: (h: (typeof history)[number]) => number,
    fallback: number
  ): (number | null)[] => {
    if (history.length <= 1 || chartDates.length === 0) {
      return new Array(pointCount).fill(fallback);
    }
    const sorted = [...history].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const values: (number | null)[] = new Array(pointCount).fill(null);
    let idx = 0;
    let current: number | null = null;
    for (let i = 0; i < chartDates.length; i++) {
      const chartTs = chartDates[i];
      while (
        idx < sorted.length &&
        new Date(sorted[idx].date).getTime() <= chartTs
      ) {
        current = selector(sorted[idx]);
        idx++;
      }
      values[i] = current;
    }
    return values;
  };

  const mcLine = (
    data: (number | null)[],
    label: string,
    color: string,
    dash: number[]
  ) => ({
    label,
    data,
    borderColor: color,
    borderWidth: 2,
    borderDash: dash,
    pointRadius: 3,
    fill: false,
    tension: 0.2,
    spanGaps: false,
  });

  return [
    mcLine(
      seriesFor((h) => h.p50TotalHours, forecast.p50TotalHours),
      `MC P50 (${forecast.p50TotalHours.toFixed(0)}h)`,
      "rgba(108, 120, 240, 0.95)",
      []
    ),
    mcLine(
      seriesFor((h) => h.p80TotalHours, forecast.p80TotalHours),
      `MC P80 (${forecast.p80TotalHours.toFixed(0)}h)`,
      "rgba(108, 120, 240, 0.6)",
      [6, 4]
    ),
    mcLine(
      seriesFor((h) => h.p95TotalHours, forecast.p95TotalHours),
      `MC P95 (${forecast.p95TotalHours.toFixed(0)}h)`,
      "rgba(108, 120, 240, 0.35)",
      [2, 4]
    ),
  ];
};

const ProjectProgressReport = ({
  selectedProject,
  selectedMilestone,
}: ProjectProgressReportProps) => {
  const [chart, setChart] = useState<Chart | null>(null);

  useEffect(() => {
    if (!selectedMilestone) {
      return () => {};
    }

    const chartItem = document.getElementById("chart-metrics") as ChartItem;
    const labelCount = selectedMilestone?.totalEstimate?.length ?? 0;

    if (chart != null) {
      chart.data.labels =
        selectedMilestone?.totalEstimate?.map((item) =>
          new Date(item.date).toLocaleDateString()
        ) || [];
      chart.data.datasets = [
        {
          label: "Total Estimate",
          tension: 0.4,
          data:
            selectedMilestone?.totalEstimate?.map((item) => item.value) || [],
          borderColor: "rgba(75, 192, 192, 1)",
          fill: false,
        },
        {
          label: "Actual Burnup",
          tension: 0.4,
          data:
            selectedMilestone?.actualBurnUp?.map((item) => item.value) || [],
          borderColor: "rgba(255, 99, 132, 1)",
          fill: false,
        },
        {
          label: "Estimate Burnup",
          tension: 0.4,
          data:
            selectedMilestone?.estimateBurnUp?.map((item) => item.value) || [],
          borderColor: "rgba(255, 159, 64, 1)",
          fill: false,
        },
        ...buildLegacyEstimateDataset(selectedMilestone),
        {
          label: "Total Task Estimate",
          tension: 0.4,
          data:
            selectedMilestone?.totalTaskEstimates?.map((item) => item.value) ||
            [],
          borderColor: "rgba(54, 162, 235, 1)",
          fill: false,
        },
        {
          label: "Total Of Estimated Completed",
          tension: 0.4,
          data:
            selectedMilestone?.totalOfEstimatedCompleted?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(255, 105, 180, 1)",
          fill: false,
        },
        {
          label: "Total Tasks",
          tension: 0.4,
          data: selectedMilestone?.totalTasks?.map((item) => item.value) || [],
          borderColor: "rgba(201, 203, 207, 1)",
          fill: false,
        },
        ...buildForecastDatasets(selectedMilestone, labelCount),
      ];
      chart.update();
    } else {
      const newChart = new Chart(chartItem, {
        type: "line",
        data: {
          labels: selectedMilestone.totalEstimate.map((item) =>
            new Date(item.date).toLocaleDateString()
          ),
          datasets: [
            {
              label: "Total Estimate",
              tension: 0.4,
              data: selectedMilestone.totalEstimate.map((item) => item.value),
              borderColor: "rgba(75, 192, 192, 1)",
              fill: false,
            },
            {
              label: "Actual Burnup",
              tension: 0.4,
              data: selectedMilestone.actualBurnUp.map((item) => item.value),
              borderColor: "rgba(255, 99, 132, 1)",
              fill: false,
            },
            {
              label: "Estimate Burnup",
              tension: 0.4,
              data: selectedMilestone.estimateBurnUp.map((item) => item.value),
              borderColor: "rgba(255, 159, 64, 1)",
              fill: false,
            },
            ...buildLegacyEstimateDataset(selectedMilestone),
            {
              label: "Total Task Estimate",
              tension: 0.4,
              data: selectedMilestone.totalTaskEstimates.map(
                (item) => item.value
              ),
              borderColor: "rgba(54, 162, 235, 1)",
              fill: false,
            },
            {
              label: "Total Of Estimated Completed",
              tension: 0.4,
              data: selectedMilestone.totalOfEstimatedCompleted.map(
                (item) => item.value
              ),
              borderColor: "rgba(255, 105, 180, 1)",
              fill: false,
            },
            {
              label: "Total Tasks",
              tension: 0.4,
              data: selectedMilestone.totalTasks.map((item) => item.value),
              borderColor: "rgba(201, 203, 207, 1)",
              fill: false,
            },
            ...buildForecastDatasets(selectedMilestone, labelCount),
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                boxWidth: 12,
                padding: 15,
                font: {
                  size: 11,
                },
              },
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 12,
              titleFont: {
                size: 13,
              },
              bodyFont: {
                size: 12,
              },
              cornerRadius: 4,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
              },
            },
            x: {
              grid: {
                display: false,
              },
            },
          },
        },
      });
      setChart(newChart);
    }

    return () => {};
  }, [selectedMilestone, chart]);

  useEffect(() => {
    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chart]);

  const ratio =
    (selectedMilestone?.actualBurnUp?.[
      (selectedMilestone?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.totalTaskEstimates?.[
      (selectedMilestone?.totalTaskEstimates?.length || 1) - 1
    ]?.value || 1);

  const actualToTotalEstimateRatio =
    (selectedMilestone?.actualBurnUp?.[
      (selectedMilestone?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.totalEstimate?.[
      (selectedMilestone?.totalEstimate?.length || 1) - 1
    ]?.value || 1);

  const actualToEstimateBurnupRatio =
    (selectedMilestone?.actualBurnUp?.[
      (selectedMilestone?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.estimateBurnUp?.[
      (selectedMilestone?.estimateBurnUp?.length || 1) - 1
    ]?.value || 1);

  let color =
    (ratio <= 1 && "rgba(75, 192, 192, 1)") ||
    (ratio > 1 && ratio <= 1.2 && "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  let actualToTotalEstimateColor =
    (actualToTotalEstimateRatio <= 1 && "rgba(75, 192, 192, 1)") ||
    (actualToTotalEstimateRatio > 1 &&
      actualToTotalEstimateRatio <= 1.2 &&
      "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  let actualToEstimateBurnupColor =
    (actualToEstimateBurnupRatio <= 1 && "rgba(75, 192, 192, 1)") ||
    (actualToEstimateBurnupRatio > 1 &&
      actualToEstimateBurnupRatio <= 1.2 &&
      "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  const lastKnownDate =
    selectedMilestone?.totalEstimate?.[
      (selectedMilestone?.totalEstimate?.length || 1) - 1
    ]?.date;

  const formattedLastKnownDate = lastKnownDate
    ? new Date(lastKnownDate).toLocaleString()
    : "No data available";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        mt: 3,
      }}
    >
      {/* Chart Section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
          background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: { xs: "350px", sm: "400px", md: "450px" },
            position: "relative",
          }}
        >
          <Box
            component="canvas"
            id="chart-metrics"
            sx={{
              height: {
                xs: "350px !important",
                sm: "400px !important",
                md: "450px !important",
              },
            }}
          />
        </Box>
      </Paper>

      {/* Metrics Cards Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" },
          gap: 2,
        }}
      >
        {/* Latest Values Card */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Latest Values
            </Typography>
            <TaskProgressStrip
              total={selectedMilestone?.totalTaskCount ?? 0}
              open={selectedMilestone?.openTaskCount ?? 0}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(75, 192, 192, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(75, 192, 192, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {selectedMilestone
                    ? (
                        selectedMilestone.totalEstimate[
                          (selectedMilestone.totalEstimate.length || 1) - 1
                        ]?.value || 0
                      ).toFixed(2)
                    : "N/A"}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(255, 99, 132, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(255, 99, 132, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual burnup
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual burnup"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.actualBurnUp?.[
                      (selectedMilestone?.actualBurnUp?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(255, 159, 64, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(255, 159, 64, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Estimate burnup
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Estimate burnup"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.estimateBurnUp?.[
                      (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              {!selectedMilestone?.forecast && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderLeft: "3px solid rgba(153, 102, 255, 1)",
                    pl: 1.5,
                    backgroundColor: "rgba(153, 102, 255, 0.05)",
                    borderRadius: "0 4px 4px 0",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Est. Total hours (ratio)
                    </Typography>
                    <Tooltip title={METRIC_TOOLTIPS["Est. Total hours"]} arrow>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="body1" fontWeight={600}>
                    {(
                      selectedMilestone?.estimatedTotalHoursNeeded?.[
                        (selectedMilestone?.estimatedTotalHoursNeeded?.length || 1) -
                          1
                      ]?.value || 0
                    ).toFixed(2)}
                    h
                  </Typography>
                </Box>
              )}

              {selectedMilestone?.forecast && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    py: 1,
                    borderLeft: "3px solid rgba(108, 120, 240, 1)",
                    pl: 1.5,
                    backgroundColor: "rgba(108, 120, 240, 0.06)",
                    borderRadius: "0 4px 4px 0",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Monte Carlo forecast (total hours)
                      </Typography>
                      <Tooltip
                        title={`Overrun-multiplier model: for each closed task, compute actual/estimate ratio; sample those to project remaining estimated hours. ${selectedMilestone.forecast.overrunSamplesUsed} overrun samples, ${selectedMilestone.forecast.velocitySamplesUsed} velocity samples. Mean overrun: ${selectedMilestone.forecast.overrunMultiplierMean.toFixed(2)}× (±${selectedMilestone.forecast.overrunMultiplierStdDev.toFixed(2)}).`}
                        arrow
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography variant="body1" fontWeight={700}>
                      {selectedMilestone.forecast.p50TotalHours.toFixed(1)}h
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: "text.secondary", ml: 0.5 }}
                      >
                        (P50)
                      </Typography>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    <span>
                      P80 {selectedMilestone.forecast.p80TotalHours.toFixed(1)}h ·
                      P95 {selectedMilestone.forecast.p95TotalHours.toFixed(1)}h
                    </span>
                    <span>
                      done {selectedMilestone.forecast.actualHoursSoFar.toFixed(0)}h
                      / remaining est. {selectedMilestone.forecast.remainingEstimateHours.toFixed(0)}h
                    </span>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    <span>
                      ETA P50 {new Date(selectedMilestone.forecast.p50Date).toLocaleDateString()} ·
                      P80 {new Date(selectedMilestone.forecast.p80Date).toLocaleDateString()}
                    </span>
                    <span>
                      vs plan {selectedMilestone.forecast.sprintTotalEstimateHours.toFixed(0)}h
                    </span>
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(54, 162, 235, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(54, 162, 235, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total Task Estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total Task Estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.totalTaskEstimates?.[
                      (selectedMilestone?.totalTaskEstimates?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(255, 105, 180, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(255, 105, 180, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total Of Est. Completed
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total Of Est. Completed"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.totalOfEstimatedCompleted?.[
                      (selectedMilestone?.totalOfEstimatedCompleted?.length || 1) -
                        1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(201, 203, 207, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(201, 203, 207, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total Tasks
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total Tasks"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.totalTasks?.[
                      (selectedMilestone?.totalTasks?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(0)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(158, 158, 158, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(158, 158, 158, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Open / Total tasks
                  </Typography>
                  <Tooltip
                    title="Tasks without a GitLab/GitHub close date vs. all tasks imported for this milestone. Updated on every sync."
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }}
                    />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {selectedMilestone?.openTaskCount ?? 0}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: "text.secondary", mx: 0.5 }}
                  >
                    /
                  </Typography>
                  {selectedMilestone?.totalTaskCount ?? 0}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(0, 0, 133, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(0, 0, 133, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Gain From Estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Gain From Estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={
                    (
                      (selectedMilestone?.estimateBurnUp?.[
                        (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                      ]?.value || 0) -
                      (selectedMilestone?.actualBurnUp?.[
                        (selectedMilestone?.actualBurnUp?.length || 1) - 1
                      ]?.value || 0)
                    ).toFixed(2) === "0.00"
                      ? "0h"
                      : `${(
                          (selectedMilestone?.estimateBurnUp?.[
                            (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                          ]?.value || 0) -
                          (selectedMilestone?.actualBurnUp?.[
                            (selectedMilestone?.actualBurnUp?.length || 1) - 1
                          ]?.value || 0)
                        ).toFixed(2)}h`
                  }
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      (selectedMilestone?.estimateBurnUp?.[
                        (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                      ]?.value || 0) -
                        (selectedMilestone?.actualBurnUp?.[
                          (selectedMilestone?.actualBurnUp?.length || 1) - 1
                        ]?.value || 0) ===
                      0
                        ? "rgba(75, 192, 192, 0.15)"
                        : (selectedMilestone?.estimateBurnUp?.[
                            (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                          ]?.value || 0) -
                            (selectedMilestone?.actualBurnUp?.[
                              (selectedMilestone?.actualBurnUp?.length || 1) - 1
                            ]?.value || 0) >
                          0
                        ? "rgba(75, 192, 192, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color:
                      (selectedMilestone?.estimateBurnUp?.[
                        (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                      ]?.value || 0) -
                        (selectedMilestone?.actualBurnUp?.[
                          (selectedMilestone?.actualBurnUp?.length || 1) - 1
                        ]?.value || 0) >=
                      0
                        ? "rgba(75, 192, 192, 1)"
                        : "rgba(255, 99, 132, 1)",
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Ratios Card */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
              Ratios
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual / Total Task Est.
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual / Total Task Est."]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={ratio.toFixed(2)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      ratio <= 1
                        ? "rgba(75, 192, 192, 0.15)"
                        : ratio <= 1.2
                        ? "rgba(255, 159, 64, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color: color,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual / Total Estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual / Total Estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={actualToTotalEstimateRatio.toFixed(2)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      actualToTotalEstimateRatio <= 1
                        ? "rgba(75, 192, 192, 0.15)"
                        : actualToTotalEstimateRatio <= 1.2
                        ? "rgba(255, 159, 64, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color: actualToTotalEstimateColor,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual / Estimate Burnup
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual / Estimate Burnup"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={actualToEstimateBurnupRatio.toFixed(2)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      actualToEstimateBurnupRatio <= 1
                        ? "rgba(75, 192, 192, 0.15)"
                        : actualToEstimateBurnupRatio <= 1.2
                        ? "rgba(255, 159, 64, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color: actualToEstimateBurnupColor,
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Last Known Date Card */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
              Last Known Date
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Date and time stamp:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formattedLastKnownDate}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ProjectProgressReport;
